import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import type { StaffLanguage } from "../../../packages/common/src/schemas/auth.ts";
import { useAuth } from "#auth.tsx";
import type { OrderState } from "#orders.ts";

const english = {
  "common.done": "Done",
  "history.selectPage": "Select all orders on this page",
  "history.selectOrder": "Select order {label}",
  "history.items": "Items",
  "history.quickStatus": "Quick status filter",
  "history.search": "Search loaded orders",
  "history.searchAction": "Search",
  "history.bulk": "Update selected ({count})",
  "history.loaded": "{count} matching loaded orders",
  "history.moreAvailable": "More history available — load older orders to search further",
  "history.clearSearch": "Clear search",
  "history.clearSelection": "Clear {count} selected",
  "history.noMatches": "No matching loaded orders. Adjust filters or load older orders.",
  "history.pageSize": "Rows per page",
  "history.page": "Page {page} of {count}",
  "history.previous": "Previous page",
  "history.next": "Next page",
  "history.confirmTitle": "Update {count} selected orders",
  "history.results": "Bulk update results",
  "history.resultCount": "{updated} updated · {failed} failed · {skipped} already at this status",
  "history.failureHelp": "Some orders were not confirmed. Check their latest status before selecting them again. Successful changes have been saved.",
  "history.unchanged": "Already at this status",
  "history.conflict": "Changed elsewhere — not updated",
  "history.uncertain": "Not confirmed — check latest status",
  "history.bulkHelp": "Only the selected orders listed below will change. This does not charge, cancel or refund a Square payment.",
  "history.newStatus": "New status",
  "history.noBulkUndo": "Bulk updates do not have a single Undo button. You can correct individual orders afterwards. Changes made on another screen are protected.",
  "history.confirm": "Confirm update ({count})",
  "history.updating": "Updating {count} orders…",
  "actions.pickup": "Pickup",
  "actions.ready": "Ready",
  "actions.cashReceived": "Cash received",
  "actions.complete": "Complete",
  "actions.more": "More options",
  "actions.schedule": "Schedule",
  "actions.qr": "Generate QR",
  "actions.cancel": "Cancel",
  "toast.window": "Undo is available for 10 seconds",
  "toast.undo": "Undo",
  "toast.completed": "Order {label} completed.",
  "toast.cancelled": "Order {label} cancelled.",
  "toast.undoing": "Undoing order change…",
  "toast.restored": "Order restored to {status}.",
  "toast.conflict": "This order changed elsewhere. Undo was not applied.",
  "toast.uncertain": "Undo could not be confirmed. Check the order’s latest status.",
  "toast.delayed": "Order restored. Live updates are delayed; check the other kitchen screens.",
  "toast.dismiss": "Dismiss order notification",
  "nav.skip": "Skip to orders",
  "nav.close": "Close navigation",
  "nav.open": "Open navigation",
  "nav.staff": "Staff navigation",
  "nav.operations": "Operations",
  "nav.orders": "Orders",
  "nav.preorders": "Preorders",
  "nav.history": "History / POS",
  "nav.dashboard": "Dashboard",
  "nav.customerDisplay": "Customer display",
  "nav.settings": "Settings",
  "nav.menu": "Menu",
  "nav.expand": "Expand sidebar",
  "nav.collapse": "Collapse sidebar",
  "nav.live": "Live updates connected",
  "nav.offline": "Offline",
  "nav.connecting": "Connecting to live updates",
  "nav.account": "Account & devices",
  "nav.logout": "Log out",
  "login.eyebrow": "Pocket Rice operations",
  "login.title": "Welcome back",
  "login.description": "Sign in to manage today’s orders and upcoming preorders.",
  "login.username": "Username",
  "login.password": "Password",
  "login.signIn": "Sign in",
  "login.signingIn": "Signing in…",
  "login.passkey": "Sign in with a passkey",
  "login.passkeyPending": "Waiting for passkey…",
  "settings.eyebrow": "Operations",
  "settings.title": "Settings",
  "settings.description": "Manage language, the online store, and Square connection.",
  "settings.languageTitle": "Language",
  "settings.languageDescription": "Choose the language used for Landpad controls and settings.",
  "settings.languageLabel": "Interface language",
  "settings.english": "English",
  "settings.japanese": "日本語",
  "settings.languageSaving": "Saving language…",
  "settings.languageSaved": "Language saved.",
  "settings.loading": "Loading connection…",
  "settings.onlineStore": "Online store",
  "settings.onlineStoreDescription": "The existing walk-up online-store switch. Bay Grocer preorder availability follows its separate schedule.",
  "settings.unavailable": "Unavailable",
  "settings.saving": "Saving…",
  "settings.closeStore": "Close store",
  "settings.openStore": "Open store",
  "settings.square": "Square integration",
  "settings.squareConnected": "Connected to Square",
  "settings.squareDisconnected": "Square is not connected",
  "settings.reconnectSquare": "Reconnect Square",
  "settings.connectSquare": "Connect Square",
  "settings.disconnect": "Disconnect…",
  "settings.disconnectTitle": "Disconnect Square?",
  "settings.keepConnected": "Keep connected",
  "settings.disconnectSquare": "Disconnect Square",
  "settings.disconnectWarning": "Staff order access through this Square connection will stop until you reconnect.",
  "account.title": "Account & devices",
  "account.close": "Close account and devices",
  "account.signedIn": "Signed in as",
  "account.kitchenShift": "Kitchen shift access",
  "account.kitchenShiftDescription": "This device has temporary kitchen-only access. Ask an administrator for a new QR code when the shift ends.",
  "account.sessionEnds": "This temporary session ends at",
  "account.newQr": "Ask an administrator for a new QR next shift.",
  "account.credentialsLocked": "Passkeys and other sign-in credentials cannot be changed from a kitchen shift session.",
  "passkeys.title": "Passkeys",
  "passkeys.description": "Use Face ID, Touch ID, Windows Hello, or a security key to sign in without entering a password.",
  "passkeys.name": "Passkey name",
  "passkeys.add": "Add passkey",
  "passkeys.adding": "Adding…",
  "passkeys.loading": "Loading passkeys…",
  "passkeys.none": "No passkeys added yet.",
  "passkeys.remove": "Remove",
  "passkeys.added": "Passkey added. You can use it the next time you sign in.",
  "passkeys.removed": "Passkey removed.",
  "passkeys.addedOn": "Added",
  "passkeys.defaultName": "This device",
  "staff.title": "Kitchen staff accounts",
  "staff.description": "Give each kitchen account a username. It can only open Orders and needs an administrator to start each shift.",
  "staff.username": "Username",
  "staff.create": "Create account",
  "staff.creating": "Creating…",
  "staff.loading": "Loading kitchen accounts…",
  "staff.none": "No kitchen accounts yet.",
  "staff.role": "Kitchen staff",
  "staff.startShift": "Start 8-hour shift",
  "staff.creatingQr": "Creating QR…",
  "staff.remove": "Remove account",
  "staff.removing": "Removing…",
  "staff.cancel": "Cancel",
  "staff.ready": "is ready. Start a shift below when they need access.",
  "staff.removed": "was removed.",
  "staff.scanOnce": "Scan once · QR expires in",
  "staff.minutes": "minute(s).",
  "staff.accessUntil": "will have kitchen-only access until",
  "staff.removeTitle": "Remove",
  "staff.removeClose": "Close account removal confirmation",
  "staff.removeWarning": "This permanently removes the kitchen account and its stored sign-in sessions. This cannot be undone.",
  "staff.keep": "Keep account",
  "availability.eyebrow": "Today",
  "availability.title": "Menu",
  "availability.description": "Switch off anything that has sold out or isn't on today at this market. Its kiosks and online orders update straight away. Stock counts in Square are ignored.",
  "availability.market": "Market",
  "availability.noMarkets": "There are no Square locations to sell from yet.",
  "availability.loading": "Loading the menu…",
  "availability.retry": "Try again",
  "availability.empty": "There are no items in the Square catalog yet.",
  "availability.allOn": "Everything is on sale.",
  "availability.offCount": "{count} switched off",
  "availability.allBackOn": "Switch everything back on",
  "availability.off": "Off",
  "availability.sell": "Sell {name}",
  "availability.failed": "That change wasn't saved. Check your connection and try again.",
  "availability.delayed": "Saved. Kiosks may take a moment to catch up.",
  "dietary.vegan": "Vegan",
  "dietary.vegetarian": "Vegetarian",
  "dietary.gluten-free": "Gluten free",
  "dietary.dairy-free": "Dairy free",
  "dietary.nut-free": "Nut free",
  "dietary.halal": "Halal",
  "dietary.kosher": "Kosher",
  "presets.title": "Kitchen instruction presets",
  "presets.description": "Create reusable item instructions for the kitchen. Existing orders are never changed.",
  "presets.loading": "Loading presets…",
  "presets.none": "No presets saved. Staff can still enter custom notes.",
  "presets.edit": "Edit preset",
  "presets.add": "Add preset",
  "presets.label": "Label",
  "presets.colour": "Tag colour",
  "presets.save": "Save preset",
  "presets.saving": "Saving…",
  "presets.reload": "Reload presets",
  "presets.editAction": "Edit",
  "presets.removeAction": "Remove",
  "presets.cancelEdit": "Cancel edit",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.remove": "Remove",
  "common.back": "Back",
  "common.closeDialog": "Close dialog",
  "orders.active": "Active",
  "orders.modifiers": "Modifiers",
  "orders.scheduled": "Scheduled",
  "orders.pending": "Pending",
  "orders.complete": "Complete",
  "orders.cancelled": "Cancelled",
  "orders.refunded": "Refunded",
  "orders.orderStatus": "Order status",
  "orders.loading": "Loading orders…",
  "orders.loadOlder": "Load older orders",
  "orders.loadingOlder": "Loading…",
  "orders.noScheduled": "No scheduled orders",
  "orders.noPending": "No pending orders",
  "orders.noView": "No orders in this view",
  "orders.pickup": "Pickup",
  "orders.created": "Created",
  "orders.status": "Status",
  "orders.total": "Total",
  "orders.details": "Details",
  "orders.filters": "Filters",
  "orders.closeFilters": "Close filters",
  "orders.live": "Orders update live.",
  "orders.reconnecting": "Reconnecting to live updates. Reload the page if orders look out of date.",
  "orders.all": "All",
  "orders.reset": "Reset",
  "orders.applyFilters": "Apply filters",
  "orders.open": "Open order",
  "orders.historyTitle": "Order history / POS",
  "orders.kitchenTitle": "Kitchen orders",
  "orders.kitchenQueues": "Kitchen queues",
  "orders.count": "{count} orders",
  "orders.paymentDevice": "Payment device",
  "orders.allDevices": "All devices",
  "orders.unassignedDevice": "Unassigned",
  "orders.deviceCount": "{count} devices",
  "orders.locations": "Locations / markets",
  "orders.locationsLoading": "Loading locations…",
  "orders.awaitingPickup": "Waiting for pickup — already prepared",
  "orders.preparing": "Being prepared",
  "orders.allLocations": "All locations",
  "orders.unassignedLocation": "Unknown location",
  "orders.locationsUnavailable": "Could not refresh locations. Saved locations and selections are still available; reopen Filters to retry.",
  "orders.locationHistoryHelp": "Filters apply to loaded history. Load more orders to search further back.",
  "orders.activeHelp": "Active includes open, in-progress, and ready orders, oldest first. Scheduled orders use their own tab until 15 minutes before pickup.",
  "orders.historyHelp": "History shows newest orders first.",
  "orders.openState": "Open",
  "orders.inProgressState": "In progress",
  "orders.readyState": "Ready",
  "orders.paymentPendingState": "Payment pending",
  "orders.paymentFailedState": "Payment failed",
  "orders.unreadable": "Item details could not be read. Check this order in Square before preparing it.",
  "orders.item": "Item",
  "paymentDevices.title": "Payment devices",
  "paymentDevices.description": "Name each Square device after its market or location. These names appear in the Orders filter.",
  "paymentDevices.loading": "Loading payment devices…",
  "paymentDevices.none": "Devices appear here after Landpad receives a paid Square order.",
  "paymentDevices.name": "Device name",
  "paymentDevices.save": "Save name",
  "paymentDevices.squareId": "Square ID …{id}",
  "paymentDevices.rename": "Rename",
  "dialog.save": "Save",
  "dialog.saving": "Saving…",
  "dialog.cancelOrder": "Cancel order",
  "dialog.cancelling": "Cancelling…",
  "dialog.completeOrder": "Complete order",
  "dialog.recordCash": "Record cash payment for this kiosk order",
  "dialog.cashRecorded": "Cash recorded. The order moves to paid once Square confirms.",
  "dialog.completing": "Completing…",
  "dialog.schedule": "Schedule order",
  "dialog.changeSchedule": "Change schedule",
  "dialog.addInfo": "Add order information",
  "dialog.editInfo": "Edit order information",
  "dialog.generateQr": "Generate customer tracking QR",
  "dialog.generating": "Generating…",
  "dialog.pickup": "Pickup name or number",
  "dialog.savePickup": "Save pickup info",
  "dialog.saveSchedule": "Save schedule",
  "dialog.clearSchedule": "Clear schedule",
  "dialog.moreOptions": "More order options",
  "dialog.setPickup": "Set pickup name / number",
  "dialog.cancelPrompt": "Cancel this order in the kitchen queue?",
  "dialog.orderTitle": "Order {label}",
  "dialog.orderInformationTitle": "Order information — {label}",
  "dialog.instructionsTitle": "Instructions for {item}",
  "dialog.closeOrder": "Close order details",
  "dialog.squareOrder": "Square order: {id}",
  "dialog.scheduledFor": "Scheduled {time}",
  "dialog.trackingExpires": "Scan to track this order. Expires {time}.",
  "dialog.moveInProgress": "Move to in progress",
  "dialog.markStatus": "Mark {status}",
  "dialog.lineInstructionsTitle": "Instructions for {item}, item {number}",
  "dialog.editItemInstructions": "Add or edit item instructions",
  "dialog.unidentifiableItem": "This item cannot be identified uniquely; use order information instead",
  "dialog.orderNotFound": "This order was not found. Refresh the order list.",
  "dialog.saveDelayed": "Order saved. Live updates could not be delivered; refresh the other kitchen screens.",
  "dialog.pickupRequired": "Enter a pickup name or number.",
  "dialog.futureTimeRequired": "Choose a future preparation time.",
  "dialog.pickupHelp": "Enter the name or ticket number the kitchen should call out.",
  "dialog.scheduleHelp": "Future orders appear in the kitchen 15 minutes before this time. The time picker uses this device’s timezone.",
  "dialog.preparationTime": "Preparation date and time",
  "dialog.cancelWarning": "This changes its operational status. It does not refund or cancel a Square payment.",
  "dialog.cancelAction": "Cancel order…",
  "notes.quick": "Quick instructions",
  "notes.wholeOrder": "Whole order",
  "notes.editOrder": "Order tags & notes",
  "notes.takeaway": "Takeaway",
  "notes.cleanBefore": "Clean before preparing (vegan)",
  "notes.wholeOrderHelp": "These tags apply to every item. Tap to add or remove; save when finished. For item-specific changes, go back and tap that item.",
  "notes.cleanDisclaimer": "Cleaning is a preparation request, not an allergen-free guarantee. Confirm any allergy requirements with the customer.",
  "notes.orderPresetsUnavailable": "Saved presets could not be loaded. Starter tags, existing tags and notes are still available.",
  "notes.legacyPreserved": "Earlier counted instructions are preserved separately and will not be changed by this save.",
  "notes.orderText": "Order information",
  "notes.orderPlaceholder": "For example: Please pack this order in two bags.",
  "notes.orderCharacters": "{count}/1500 characters. Internal staff information only.",
  "notes.orderLimit": "Use up to 12 whole-order tags and 1,500 characters of order information.",
  "notes.loading": "Loading presets…",
  "notes.forItem": "For this item",
  "notes.saveItem": "Save item instructions",
  "notes.saveOrder": "Save order information",
  "notes.saving": "Saving…",
  "preorders.title": "Preorders",
  "preorders.status": "Preorder status",
  "preorders.loading": "Loading preorders…",
  "preorders.pickup": "Pickup",
  "preorders.total": "Total",
  "preorders.markCollected": "Mark collected",
  "preorders.completing": "Completing…",
  "dashboard.eyebrow": "Last 24 hours",
  "dashboard.title": "Dashboard",
  "dashboard.description": "Orders, revenue, and average order value.",
  "dashboard.loading": "Loading analytics…",
  "dashboard.totalOrders": "Total orders",
  "dashboard.revenue": "Revenue",
  "dashboard.averageOrder": "Average order",
  "dashboard.peakHour": "Peak hour",
  "dashboard.hourly": "Hourly breakdown",
  "dashboard.hour": "Hour",
  "dashboard.orders": "Orders",
  "dashboard.average": "Average",
  "dashboard.refresh": "Refresh",
  "dashboard.ordersByHour": "Orders by hour",
  "dashboard.revenueByHour": "Revenue by hour",
  "dashboard.averageByHour": "Average order value",
} as const;

type TranslationKey = keyof typeof english;

const orderStateTranslationKeys = {
  order_open: "orders.openState",
  order_paid_and_in_progress: "orders.inProgressState",
  order_ready: "orders.readyState",
  order_completed: "orders.complete",
  order_canceled: "orders.cancelled",
  order_refunded: "orders.refunded",
} satisfies Record<OrderState, TranslationKey>;

const japanese: Record<TranslationKey, string> = {
  "common.done": "閉じる",
  "history.selectPage": "このページの注文をすべて選択",
  "history.selectOrder": "注文 {label} を選択",
  "history.items": "商品",
  "history.quickStatus": "ステータスで絞り込み",
  "history.search": "読み込み済みの注文を検索",
  "history.searchAction": "検索",
  "history.bulk": "選択した注文を更新（{count}件）",
  "history.loaded": "読み込み済みの注文 {count}件が一致",
  "history.moreAvailable": "過去の注文を読み込むと、さらに検索できます",
  "history.clearSearch": "検索をクリア",
  "history.clearSelection": "{count}件の選択を解除",
  "history.noMatches": "一致する注文がありません。絞り込みを変更するか、過去の注文を読み込んでください。",
  "history.pageSize": "表示件数",
  "history.page": "{count}ページ中 {page}ページ",
  "history.previous": "前のページ",
  "history.next": "次のページ",
  "history.confirmTitle": "選択した注文 {count}件を更新",
  "history.results": "一括更新の結果",
  "history.resultCount": "更新 {updated}件・失敗 {failed}件・変更不要 {skipped}件",
  "history.failureHelp": "確認できなかった注文があります。再選択する前に最新の状態を確認してください。成功した変更は保存されています。",
  "history.unchanged": "すでにこのステータスです",
  "history.conflict": "他の端末で変更済み — 更新していません",
  "history.uncertain": "未確認 — 最新の状態を確認してください",
  "history.bulkHelp": "以下に表示された選択済みの注文のみ変更します。Square決済の請求・取消・返金は行いません。",
  "history.newStatus": "変更後のステータス",
  "history.noBulkUndo": "一括更新には「元に戻す」ボタンがありません。後から各注文を個別に修正できます。他の端末で行われた変更は保護されます。",
  "history.confirm": "更新を確定（{count}件）",
  "history.updating": "{count}件を更新中…",
  "actions.pickup": "受取名・番号",
  "actions.ready": "準備完了",
  "actions.cashReceived": "現金受領",
  "actions.complete": "受渡し完了",
  "actions.more": "その他",
  "actions.schedule": "予約時間",
  "actions.qr": "QRを作成",
  "actions.cancel": "取消",
  "toast.window": "10秒間、元に戻せます",
  "toast.undo": "元に戻す",
  "toast.completed": "注文 {label} を完了しました。",
  "toast.cancelled": "注文 {label} をキャンセルしました。",
  "toast.undoing": "注文の変更を元に戻しています…",
  "toast.restored": "注文を「{status}」に戻しました。",
  "toast.conflict": "この注文は別の画面で変更されました。元に戻す操作は適用されませんでした。",
  "toast.uncertain": "元に戻せたか確認できません。注文の最新ステータスを確認してください。",
  "toast.delayed": "注文を元に戻しました。ライブ更新が遅れています。他のキッチン画面を確認してください。",
  "toast.dismiss": "注文の通知を閉じる",
  "nav.skip": "注文一覧へ移動",
  "nav.close": "ナビゲーションを閉じる",
  "nav.open": "ナビゲーションを開く",
  "nav.staff": "スタッフナビゲーション",
  "nav.operations": "オペレーション",
  "nav.orders": "注文",
  "nav.preorders": "予約注文",
  "nav.history": "履歴 / POS",
  "nav.dashboard": "ダッシュボード",
  "nav.customerDisplay": "お客様用表示",
  "nav.settings": "設定",
  "nav.menu": "メニュー",
  "nav.expand": "サイドバーを展開",
  "nav.collapse": "サイドバーを折りたたむ",
  "nav.live": "ライブ更新に接続済み",
  "nav.offline": "オフライン",
  "nav.connecting": "ライブ更新に接続中",
  "nav.account": "アカウントと端末",
  "nav.logout": "ログアウト",
  "login.eyebrow": "Pocket Rice オペレーション",
  "login.title": "おかえりなさい",
  "login.description": "本日の注文と今後の予約注文を管理するにはログインしてください。",
  "login.username": "ユーザー名",
  "login.password": "パスワード",
  "login.signIn": "ログイン",
  "login.signingIn": "ログイン中…",
  "login.passkey": "パスキーでログイン",
  "login.passkeyPending": "パスキーを待っています…",
  "settings.eyebrow": "オペレーション",
  "settings.title": "設定",
  "settings.description": "言語、オンラインストア、Square接続を管理します。",
  "settings.languageTitle": "言語",
  "settings.languageDescription": "Landpadの操作と設定に使用する言語を選択します。",
  "settings.languageLabel": "表示言語",
  "settings.english": "English",
  "settings.japanese": "日本語",
  "settings.languageSaving": "言語を保存中…",
  "settings.languageSaved": "言語を保存しました。",
  "settings.loading": "接続を読み込み中…",
  "settings.onlineStore": "オンラインストア",
  "settings.onlineStoreDescription": "店頭オンラインストアの切り替えです。Bay Grocerの予約注文は別のスケジュールに従います。",
  "settings.unavailable": "利用不可",
  "settings.saving": "保存中…",
  "settings.closeStore": "ストアを閉じる",
  "settings.openStore": "ストアを開く",
  "settings.square": "Square連携",
  "settings.squareConnected": "Squareに接続済み",
  "settings.squareDisconnected": "Squareに接続されていません",
  "settings.reconnectSquare": "Squareに再接続",
  "settings.connectSquare": "Squareに接続",
  "settings.disconnect": "接続解除…",
  "settings.disconnectTitle": "Squareの接続を解除しますか？",
  "settings.keepConnected": "接続を維持",
  "settings.disconnectSquare": "Squareの接続を解除",
  "settings.disconnectWarning": "再接続するまで、このSquare接続を使ったスタッフの注文アクセスは停止します。",
  "account.title": "アカウントと端末",
  "account.close": "アカウントと端末を閉じる",
  "account.signedIn": "ログイン中",
  "account.kitchenShift": "キッチンシフトアクセス",
  "account.kitchenShiftDescription": "この端末には一時的なキッチン専用アクセスがあります。シフト終了後は管理者に新しいQRコードを依頼してください。",
  "account.sessionEnds": "この一時セッションの終了時刻:",
  "account.newQr": "次のシフトでは管理者に新しいQRを依頼してください。",
  "account.credentialsLocked": "キッチンシフトのセッションでは、パスキーなどのログイン認証情報を変更できません。",
  "passkeys.title": "パスキー",
  "passkeys.description": "Face ID、Touch ID、Windows Hello、またはセキュリティキーでパスワードなしにログインできます。",
  "passkeys.name": "パスキー名",
  "passkeys.add": "パスキーを追加",
  "passkeys.adding": "追加中…",
  "passkeys.loading": "パスキーを読み込み中…",
  "passkeys.none": "パスキーはまだありません。",
  "passkeys.remove": "削除",
  "passkeys.added": "パスキーを追加しました。次回からログインに使用できます。",
  "passkeys.removed": "パスキーを削除しました。",
  "passkeys.addedOn": "追加日",
  "passkeys.defaultName": "この端末",
  "staff.title": "キッチンスタッフアカウント",
  "staff.description": "各キッチンアカウントにユーザー名を設定します。注文画面のみ利用でき、各シフト開始時に管理者の操作が必要です。",
  "staff.username": "ユーザー名",
  "staff.create": "アカウントを作成",
  "staff.creating": "作成中…",
  "staff.loading": "キッチンアカウントを読み込み中…",
  "staff.none": "キッチンアカウントはまだありません。",
  "staff.role": "キッチンスタッフ",
  "staff.startShift": "8時間シフトを開始",
  "staff.creatingQr": "QRを作成中…",
  "staff.remove": "アカウントを削除",
  "staff.removing": "削除中…",
  "staff.cancel": "キャンセル",
  "staff.ready": "を準備しました。アクセスが必要なときに下のボタンからシフトを開始してください。",
  "staff.removed": "を削除しました。",
  "staff.scanOnce": "一度だけスキャン · QRの有効期限:",
  "staff.minutes": "分。",
  "staff.accessUntil": "のキッチン専用アクセス終了時刻:",
  "staff.removeTitle": "削除",
  "staff.removeClose": "アカウント削除の確認を閉じる",
  "staff.removeWarning": "キッチンアカウントと保存済みログインセッションを完全に削除します。この操作は元に戻せません。",
  "staff.keep": "アカウントを保持",
  "availability.eyebrow": "本日",
  "availability.title": "メニュー",
  "availability.description": "このマーケットで売り切れや本日販売しない商品をオフにします。このマーケットのキオスクとオンライン注文にすぐ反映されます。Squareの在庫数は使用しません。",
  "availability.market": "マーケット",
  "availability.noMarkets": "販売できるSquareのロケーションがまだありません。",
  "availability.loading": "メニューを読み込み中…",
  "availability.retry": "再試行",
  "availability.empty": "Squareのカタログに商品がまだありません。",
  "availability.allOn": "すべて販売中です。",
  "availability.offCount": "{count}件をオフにしています",
  "availability.allBackOn": "すべてオンに戻す",
  "availability.off": "オフ",
  "availability.sell": "{name}を販売する",
  "availability.failed": "変更を保存できませんでした。接続を確認して、もう一度お試しください。",
  "availability.delayed": "保存しました。キオスクへの反映に少し時間がかかる場合があります。",
  "dietary.vegan": "ヴィーガン",
  "dietary.vegetarian": "ベジタリアン",
  "dietary.gluten-free": "グルテンフリー",
  "dietary.dairy-free": "乳製品不使用",
  "dietary.nut-free": "ナッツ不使用",
  "dietary.halal": "ハラール",
  "dietary.kosher": "コーシャ",
  "presets.title": "キッチン指示プリセット",
  "presets.description": "キッチン用の商品指示を再利用できるようにします。既存の注文は変更されません。",
  "presets.loading": "プリセットを読み込み中…",
  "presets.none": "保存済みプリセットはありません。カスタムメモは入力できます。",
  "presets.edit": "プリセットを編集",
  "presets.add": "プリセットを追加",
  "presets.label": "ラベル",
  "presets.colour": "タグの色",
  "presets.save": "プリセットを保存",
  "presets.saving": "保存中…",
  "presets.reload": "プリセットを再読み込み",
  "presets.editAction": "編集",
  "presets.removeAction": "削除",
  "presets.cancelEdit": "編集をキャンセル",
  "common.cancel": "キャンセル",
  "common.close": "閉じる",
  "common.remove": "削除",
  "common.back": "戻る",
  "common.closeDialog": "ダイアログを閉じる",
  "orders.active": "対応中",
  "orders.modifiers": "オプション",
  "orders.scheduled": "予約済み",
  "orders.pending": "保留中",
  "orders.complete": "完了",
  "orders.cancelled": "キャンセル済み",
  "orders.refunded": "返金済み",
  "orders.orderStatus": "注文ステータス",
  "orders.loading": "注文を読み込み中…",
  "orders.loadOlder": "過去の注文を読み込む",
  "orders.loadingOlder": "読み込み中…",
  "orders.noScheduled": "予約注文はありません",
  "orders.noPending": "保留中の注文はありません",
  "orders.noView": "この表示に注文はありません",
  "orders.pickup": "受取",
  "orders.created": "作成日時",
  "orders.status": "ステータス",
  "orders.total": "合計",
  "orders.details": "詳細",
  "orders.filters": "フィルター",
  "orders.closeFilters": "フィルターを閉じる",
  "orders.live": "注文はライブ更新されます。",
  "orders.reconnecting": "ライブ更新に再接続中です。注文が古い場合はページを再読み込みしてください。",
  "orders.all": "すべて",
  "orders.reset": "リセット",
  "orders.applyFilters": "フィルターを適用",
  "orders.open": "注文を開く",
  "orders.historyTitle": "注文履歴 / POS",
  "orders.kitchenTitle": "キッチン注文",
  "orders.kitchenQueues": "キッチン注文一覧",
  "orders.count": "{count}件の注文",
  "orders.paymentDevice": "決済端末",
  "orders.allDevices": "すべての端末",
  "orders.unassignedDevice": "端末未設定",
  "orders.deviceCount": "{count}台の端末",
  "orders.locations": "店舗・マーケット",
  "orders.locationsLoading": "店舗を読み込み中…",
  "orders.awaitingPickup": "お渡し待ち — 調理済み",
  "orders.preparing": "調理中",
  "orders.allLocations": "すべての店舗",
  "orders.unassignedLocation": "店舗不明",
  "orders.locationsUnavailable": "店舗を更新できませんでした。保存済みの店舗と選択を表示しています。フィルターを開き直すと再試行します。",
  "orders.locationHistoryHelp": "読み込み済みの履歴を絞り込みます。過去の注文を探すには追加で読み込んでください。",
  "orders.activeHelp": "対応中には、受付済み・調理中・準備完了の注文が古い順に表示されます。予約注文は受取時刻の15分前まで専用タブに表示されます。",
  "orders.historyHelp": "履歴は新しい注文から表示されます。",
  "orders.openState": "受付済み",
  "orders.inProgressState": "調理中",
  "orders.readyState": "準備完了",
  "orders.paymentPendingState": "支払い待ち",
  "orders.paymentFailedState": "支払い失敗",
  "orders.unreadable": "商品詳細を読み取れませんでした。調理前にSquareでこの注文を確認してください。",
  "orders.item": "商品",
  "paymentDevices.title": "決済端末",
  "paymentDevices.description": "各Square端末にマーケット名や場所の名前を付けます。この名前は注文フィルターに表示されます。",
  "paymentDevices.loading": "決済端末を読み込み中…",
  "paymentDevices.none": "LandpadがSquareの支払い済み注文を受信すると、端末がここに表示されます。",
  "paymentDevices.name": "端末名",
  "paymentDevices.save": "名前を保存",
  "paymentDevices.squareId": "Square ID …{id}",
  "paymentDevices.rename": "名前を変更",
  "dialog.save": "保存",
  "dialog.saving": "保存中…",
  "dialog.cancelOrder": "注文をキャンセル",
  "dialog.cancelling": "キャンセル中…",
  "dialog.completeOrder": "注文を完了",
  "dialog.recordCash": "このキオスク注文の現金支払いを記録",
  "dialog.cashRecorded": "現金を記録しました。Squareの確認後に支払い済みになります。",
  "dialog.completing": "完了処理中…",
  "dialog.schedule": "注文を予約",
  "dialog.changeSchedule": "予約を変更",
  "dialog.addInfo": "注文情報を追加",
  "dialog.editInfo": "注文情報を編集",
  "dialog.generateQr": "お客様用追跡QRを作成",
  "dialog.generating": "作成中…",
  "dialog.pickup": "受取名または番号",
  "dialog.savePickup": "受取情報を保存",
  "dialog.saveSchedule": "予約を保存",
  "dialog.clearSchedule": "予約を解除",
  "dialog.moreOptions": "その他の注文オプション",
  "dialog.setPickup": "受取名 / 番号を設定",
  "dialog.cancelPrompt": "キッチン待ち行列のこの注文をキャンセルしますか？",
  "dialog.orderTitle": "注文 {label}",
  "dialog.orderInformationTitle": "注文情報 — {label}",
  "dialog.instructionsTitle": "{item}への指示",
  "dialog.closeOrder": "注文詳細を閉じる",
  "dialog.squareOrder": "Square注文: {id}",
  "dialog.scheduledFor": "予約日時: {time}",
  "dialog.trackingExpires": "スキャンして注文を追跡できます。有効期限: {time}。",
  "dialog.moveInProgress": "調理中に移動",
  "dialog.markStatus": "{status}にする",
  "dialog.lineInstructionsTitle": "{item}への指示、商品{number}",
  "dialog.editItemInstructions": "商品の指示を追加または編集",
  "dialog.unidentifiableItem": "この商品を一意に識別できません。代わりに注文情報を使用してください",
  "dialog.orderNotFound": "注文が見つかりませんでした。注文一覧を再読み込みしてください。",
  "dialog.saveDelayed": "注文を保存しました。ライブ更新を配信できなかったため、他のキッチン画面を再読み込みしてください。",
  "dialog.pickupRequired": "受取名または番号を入力してください。",
  "dialog.futureTimeRequired": "将来の調理時刻を選択してください。",
  "dialog.pickupHelp": "キッチンが呼び出す名前または整理番号を入力してください。",
  "dialog.scheduleHelp": "予約注文はこの時刻の15分前にキッチンへ表示されます。時刻選択にはこの端末のタイムゾーンが使用されます。",
  "dialog.preparationTime": "調理日時",
  "dialog.cancelWarning": "運用ステータスのみ変更されます。Squareの支払いは返金またはキャンセルされません。",
  "dialog.cancelAction": "注文をキャンセル…",
  "notes.quick": "クイック指示",
  "notes.wholeOrder": "注文全体",
  "notes.editOrder": "注文全体のタグ・メモ",
  "notes.takeaway": "持ち帰り",
  "notes.cleanBefore": "調理前に清掃（ヴィーガン対応）",
  "notes.wholeOrderHelp": "タグは全商品に適用されます。タップで追加・削除し、最後に保存してください。商品ごとの変更は戻って商品をタップしてください。",
  "notes.cleanDisclaimer": "清掃は調理上のお願いで、アレルゲン不使用を保証するものではありません。アレルギーの要望はお客様に確認してください。",
  "notes.orderPresetsUnavailable": "保存済みプリセットを読み込めませんでした。初期タグ・既存タグ・メモは利用できます。",
  "notes.legacyPreserved": "以前の数量付き指示は別に保持され、この保存では変更されません。",
  "notes.orderText": "注文情報",
  "notes.orderPlaceholder": "例：この注文を2つの袋に分けてください。",
  "notes.orderCharacters": "{count}/1500文字。スタッフ専用情報です。",
  "notes.orderLimit": "注文全体のタグは12個まで、注文情報は1500文字まで入力できます。",
  "notes.loading": "プリセットを読み込み中…",
  "notes.forItem": "この商品への指示",
  "notes.saveItem": "商品指示を保存",
  "notes.saveOrder": "注文情報を保存",
  "notes.saving": "保存中…",
  "preorders.title": "予約注文",
  "preorders.status": "予約注文ステータス",
  "preorders.loading": "予約注文を読み込み中…",
  "preorders.pickup": "受取",
  "preorders.total": "合計",
  "preorders.markCollected": "受取済みにする",
  "preorders.completing": "完了処理中…",
  "dashboard.eyebrow": "過去24時間",
  "dashboard.title": "ダッシュボード",
  "dashboard.description": "注文数、売上、平均注文額。",
  "dashboard.loading": "分析を読み込み中…",
  "dashboard.totalOrders": "注文合計",
  "dashboard.revenue": "売上",
  "dashboard.averageOrder": "平均注文額",
  "dashboard.peakHour": "ピーク時間",
  "dashboard.hourly": "時間別内訳",
  "dashboard.hour": "時間",
  "dashboard.orders": "注文数",
  "dashboard.average": "平均",
  "dashboard.refresh": "更新",
  "dashboard.ordersByHour": "時間別注文数",
  "dashboard.revenueByHour": "時間別売上",
  "dashboard.averageByHour": "時間別平均注文額",
};

/** Return one translated Landpad interface message. */
export function translate(
  language: StaffLanguage,
  key: TranslationKey,
  values: Readonly<Record<string, string | number>> = {},
): string {
  const message = language === "ja" ? japanese[key] : english[key];
  return Object.entries(values).reduce(
    (rendered, [name, value]) => rendered.replaceAll(`{${name}}`, String(value)),
    message,
  );
}

/** Return one translated operational order-state label. */
export function translateOrderStatus(
  language: StaffLanguage,
  state: OrderState,
): string {
  return translate(language, orderStateTranslationKeys[state]);
}

type LanguageContextValue = {
  readonly language: StaffLanguage;
  readonly setLanguage: (language: StaffLanguage) => ReturnType<
    ReturnType<typeof useAuth>["api"]["updateLanguage"]
  >;
  readonly t: (
    key: TranslationKey,
    values?: Readonly<Record<string, string | number>>,
  ) => string;
  readonly orderStatus: (state: OrderState) => string;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

/** Apply the signed-in user's saved language to the Landpad interface. */
export function LanguageProvider({ children }: { readonly children: ReactNode }) {
  const { api, state } = useAuth();
  const language = state.kind === "authenticated" ? state.language : "en";

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: (nextLanguage) => api.updateLanguage(nextLanguage),
        t: (key, values) => translate(language, key, values),
        orderStatus: (orderState) =>
          translateOrderStatus(language, orderState),
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

/** Read the current Landpad language and its typed translation lookup. */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("LanguageProvider is missing.");
  return context;
}
